import { IDP_URL_PARAM, REDIRECT_URI } from '../../constants/auth';
import { IdPs } from '../../constants/providers';
import { useNonceGenerator } from '../../contexts/NonceGeneratorContextProvider';
import { buildNonce } from '../../services/auth';
import CryptoJS from 'crypto-js';
import { ExclusivityType, PostAuthComponentParams } from '../../types/auth';
import { localStorageAdapter } from '../../services/storage';
import { isDevelopment } from '../../utils/env';
import SignInFailed from '../SignInFailed';
import Main from '../Main';
import ContainerLayout from '../ContainerLayout';
import usePageTitle from '../../hooks/usePageTitle';
import accountlinkConsent from '../../i18n/accountlinkConsent.messages';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { SyntheticEvent, useEffect, useState } from 'react';
import { LinkAccounts } from '../LinkAccounts';
import accountlinkConsentMessages from '../../i18n/accountlinkConsent.messages';
import AccountLinkFailed from '../../pages/AccountLinkFailed';
import { AppURL } from '../../constants/urls';
import { accountlinkfaqPageDetails } from '../../i18n/accountLinkFaqContent.messages';
import { format } from 'date-fns';
import { providerMessages } from '../../i18n/provider.messages';

import { FederatedSignInOptions } from '@aws-amplify/auth/lib-esm/types';
import logger from '../../utils/logger';
import Auth from '@aws-amplify/auth';
import { AppConfig } from '../../types/app';
import { MemoryAuthStorage } from '../../authConfig/amplify';
import { useStateStorage } from '../../contexts/StateStorageContextProvider';
import React from 'react';
import { useHistory } from 'react-router-dom';
import providersService from '../../services/providers';
import { emitAccountLinkingRUMEvents } from '../../services/emitAccountLinkingRUMEvent';
import styles from '../CardContainer.module.css';
import signinMessages from '../../i18n/signin.messages';

export function replaceUrlWithReloadSafeParams(searchParams: string) {
  const refreshParams = new URLSearchParams(searchParams);
  return `${AppURL.Login}?${refreshParams.toString()}`;
}

//Configure Amplify for Federated Sign IN
export function getAmplifyConfig(config: AppConfig) {
  const redirectUrl = `${window.location.origin}/accountlinkvalidate`;

  return {
    Auth: {
      storage: MemoryAuthStorage,
      region: config.accountLinkValidateRegion,
      userPoolId: config.accountLinkAuthUserPoolID,
      userPoolWebClientId: config.accountLinkAuthClientID,
      oauth: {
        domain: config.accountLinkAuthGandalfDomain,
        scope: ['openid'],
        responseType: 'code',
        redirectSignIn: redirectUrl,
        redirectSignOut: redirectUrl,
      },
    },
  };
}

const lastUsedToken = 'LAST_USED_TOKEN';
const lastUsedGandalfToken = 'LAST_USED_GANDALF_TOKEN';
export type AuthToken = {
  originalRequestURL: string;
  jwtToken: string;
  nonce: string;
};

export const unmarshalAuthToken = (
  state: string | undefined,
  nonce: string | undefined
): AuthToken | null => {
  if (!state || !nonce) {
    return null;
  }

  try {
    const fromBase64 = atob(state);
    const value = CryptoJS.AES.decrypt(fromBase64, nonce).toString(
      CryptoJS.enc.Utf8
    );
    const jsonParsed = JSON.parse(value);

    if (jsonParsed.nonce !== nonce) {
      return null;
    }

    return jsonParsed as AuthToken;
  } catch (err) {
    return null;
  }
};

export const marshalAuthToken = (state: AuthToken): string => {
  const jsonStringified = JSON.stringify(state);
  const encrypted = CryptoJS.AES.encrypt(jsonStringified, state.nonce);
  const base64 = btoa(encrypted.toString());

  return base64;
};

const ValidateAccountLink = ({
  user,
  onContinue,
  onCancel,
  config,
  error,
}: PostAuthComponentParams) => {
  const { formatMessage } = useIntl();
  usePageTitle(formatMessage(accountlinkConsent.pageTitle));
  const history = useHistory();
  const nonceGenerator = useNonceGenerator();
  const stateStorage = useStateStorage();
  const skipOtpValidation = false;
  const [validatedNotification, setValidatedNotification] = useState(false);

  const [linkingSpinner, setLinkingSpinner] = useState<boolean>(false);
  const [linkingSuccess, setLinkingSuccess] = useState<boolean>(false);

  let redirect_uri: URL;
  if (isDevelopment) {
    redirect_uri = new URL(
      `http://${window.location.host}/accountlinkvalidate`
    );
  } else {
    redirect_uri = new URL(
      `https://${window.location.host}/accountlinkvalidate`
    );
  }
  const provider = providersService.transformNameToIdProvider(
    user.providerName,
    config.gandalfDomain
  );

  useEffect(() => {
    window.onpageshow = function (event) {
      if (event.persisted) {
        setLinkingSpinner(false);
        history.push(replaceUrlWithReloadSafeParams(window.location.search));
      }
    };
    if (authToken.jwtToken) {
      emitAccountLinkingRUMEvents(
        authToken.jwtToken,
        'confirm_exisiting_profile_screen_shown'
      );
    }
    // eslint-disable-next-line
  }, []);

  const nonce = buildNonce(nonceGenerator);
  var urlParams = new URLSearchParams(window.location.search);
  urlParams.set('identity_provider', user.providerName);
  const authToken: AuthToken = {
    originalRequestURL: urlParams.toString(),
    jwtToken: user.jwtToken ?? '',
    nonce,
  };
  if (user.jwtToken && user.associate_to) {
    try {
      const handleFormSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        replaceUrlWithReloadSafeParams(urlParams.toString());
        emitAccountLinkingRUMEvents(
          authToken.jwtToken,
          'confirm_existing_profile_confirm_button'
        );
        setLinkingSpinner(true);

        const marshaledAuthToken = marshalAuthToken(authToken);

        localStorageAdapter.setItem(lastUsedToken, marshaledAuthToken);

        urlParams = new URLSearchParams();
        urlParams.set(IDP_URL_PARAM, user?.associate_to?.LinkedToIDP ?? '');
        urlParams.set(REDIRECT_URI, redirect_uri.toString());
        //urlParams.set('autofillEmail', user.email ?? '');
        urlParams.set('client_id', config.authClientId);
        urlParams.set('response_type', 'code');
        urlParams.set('state', nonce);

        const baseUrl = `https://${config.gandalfDomain}/oauth2/authorize`;
        const IdpURl = `${baseUrl}?${urlParams.toString()}`;

        //Configure Auth
        const amplifyConfig = getAmplifyConfig(config);
        Auth.configure(amplifyConfig.Auth);
        stateStorage.setItem(nonce, IdpURl);
        //Trying Federated SignIn with Amplify
        Auth.federatedSignIn({
          provider: user.associate_to?.LinkedToIDP,
          customState: nonce,
        } as FederatedSignInOptions).catch((error: Error) => {
          logger.debug('Failed to do federatedSignIn.', error);
        });
        setValidatedNotification(true);
      };

      return (
        <>
          <Main>
            <ContainerLayout>
              <div className={styles.heading_div}>
                <TextContent>
                  <h1>{formatMessage(accountlinkConsent.header)}</h1>
                </TextContent>
              </div>
              <Container
                header={
                  <>
                    <>
                      <Header
                        className={styles.header}
                        variant="h2"
                        headingTagOverride="h1"
                      >
                        {user.associate_to.Exclusivity ===
                        ExclusivityType.INCLUSIVE
                          ? formatMessage(
                              accountlinkConsent.confirmExistingProfileOwnershipToLinkHeader,
                              {
                                currentProvider:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                              }
                            )
                          : formatMessage(
                              accountlinkConsent.confirmExistingProfileOwnershipToTransferHeader,
                              {
                                currentProvider:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                              }
                            )}
                      </Header>
                    </>
                  </>
                }
              >
                {skipOtpValidation && validatedNotification ? (
                  <LinkAccounts
                    config={config}
                    at2Token={authToken}
                    changeSpinnerState={setLinkingSpinner}
                    changeLinkingSuccess={setLinkingSuccess}
                    isExclusive={
                      user.associate_to.Exclusivity ===
                      ExclusivityType.EXCLUSIVE
                    }
                  />
                ) : (
                  <></>
                )}

                <form onSubmit={handleFormSubmit} noValidate>
                  <Form
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          formAction="none"
                          onClick={(event) => {
                            event?.preventDefault();
                            emitAccountLinkingRUMEvents(
                              authToken.jwtToken,
                              'confirm_existing_profile_i_cant_button'
                            );
                            const marshaledAuthToken =
                              marshalAuthToken(authToken);

                            localStorageAdapter.setItem(
                              lastUsedToken,
                              marshaledAuthToken
                            );
                            localStorageAdapter.setItem(
                              lastUsedGandalfToken,
                              marshaledAuthToken
                            );
                            const urlParams = new URLSearchParams(
                              window.location.search
                            );
                            urlParams.set(
                              'identity_provider',
                              user.providerName
                            );
                            history.push({
                              pathname: AppURL.CantValidateExistingAccount,
                              search: urlParams.toString(),
                              state: {
                                customState: nonce,
                                validAccountLink: true,
                              },
                            });
                          }}
                        >
                          {formatMessage(
                            accountlinkConsent.cantConfirmExistingProfileOwnershipButton
                          )}
                        </Button>
                        <Button
                          variant="primary"
                          formAction="submit"
                          data-testid="email-submit"
                          loading={linkingSpinner}
                          iconName={linkingSuccess ? 'check' : undefined}
                        >
                          {formatMessage(
                            accountlinkConsent.confirmExistingProfileOwnershipButton,
                            {
                              currentProviderName:
                                providerMessages[
                                  user.associate_to.LinkedToIDP as IdPs
                                ]?.defaultMessage ||
                                formatMessage(
                                  signinMessages.organizationSigninButton
                                ),
                            }
                          )}
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    <SpaceBetween direction="vertical" size="s">
                      {user.associate_to.Exclusivity ===
                      ExclusivityType.INCLUSIVE ? (
                        <>
                          <TextContent>
                            {formatMessage(
                              accountlinkConsentMessages.linkingMultipleAccounts,
                              {
                                userEmail: user.email,
                                currentProviderName:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                lastUsedDate: format(
                                  new Date(
                                    user.associate_to.LinkedToLastSignedIn
                                  ),
                                  'MMMM d, yyyy'
                                ),
                                newProviderName:
                                  providerMessages[user.providerName as IdPs]
                                    ?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                b: (str) => <b>{str}</b>,
                              }
                            )}
                          </TextContent>
                          <TextContent>
                            {formatMessage(
                              accountlinkConsent.confirmExistingProfileOwnershipMessage,
                              {
                                userEmail: user.email,
                                currentProviderName:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                b: (str) => <b>{str}</b>,
                              }
                            )}
                          </TextContent>
                        </>
                      ) : (
                        <>
                          <TextContent>
                            {formatMessage(
                              accountlinkConsentMessages.linkingExclusiveSignleAccount,
                              {
                                userEmail: user.email,
                                currentProviderName:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                newProviderName:
                                  providerMessages[user.providerName as IdPs]
                                    ?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                b: (str) => <b>{str}</b>,
                              }
                            )}
                          </TextContent>
                          <TextContent>
                            {formatMessage(
                              accountlinkConsent.confirmExistingProfileOwnershipExclusiveMessage,
                              {
                                userEmail: user.email,
                                currentProviderName:
                                  providerMessages[
                                    user.associate_to.LinkedToIDP as IdPs
                                  ]?.defaultMessage ||
                                  formatMessage(
                                    signinMessages.organizationSigninButton
                                  ),
                                b: (str) => <b>{str}</b>,
                              }
                            )}
                          </TextContent>
                        </>
                      )}

                      <TextContent>
                        <Box margin={{ top: 's' }} float="left">
                          <a
                            href={`${AppURL.AccountLinkFAQ}`}
                            target="_blank"
                            data-testid="account-link-help-link"
                            rel="noopener noreferrer"
                            onClick={() => {
                              emitAccountLinkingRUMEvents(
                                authToken.jwtToken,
                                'confirm_exisiting_profile_learn_more_button'
                              );
                            }}
                          >
                            {formatMessage(
                              accountlinkfaqPageDetails.learnMoreAboutProfileLinking
                            )}
                          </a>
                        </Box>
                      </TextContent>
                    </SpaceBetween>
                  </Form>
                </form>
              </Container>
            </ContainerLayout>
          </Main>
        </>
      );
    } catch (e: any) {
      // eslint-disable-next-line
      console.log(e);
      return <AccountLinkFailed error={e} authToken={authToken.jwtToken} />;
    }
  } else {
    return (
      <SignInFailed
        error={error ?? new Error('SignIn Failed')}
        provider={provider}
      />
    );
  }
};

export default ValidateAccountLink;
